import { useCallback, useState } from "react"

import { supabase } from "@/lib/supabase"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

export const VEHICLE_COMMANDS = {
  lock: "lock-and-disable-driving",
  unlock: "unlock-and-enable-driving",
} as const

export type VehicleCommand = (typeof VEHICLE_COMMANDS)[keyof typeof VEHICLE_COMMANDS]

export type LockState = "locked" | "unlocked" | "locking" | "unlocking" | "unknown"

export interface KeylessVehicle {
  licensePlate: string
  lockState: LockState
  batteryPercent: number | null
}

interface KeylessResponse {
  vehicle: KeylessVehicle
}

const invokeKeyless = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke<KeylessResponse>("keyless-pin-portal", {
    body,
  })

  if (error) {
    throw new Error(await getSupabaseFunctionErrorMessage(error))
  }

  return data.vehicle
}

/**
 * Holds the PIN for the life of the session so lock and unlock can be re-authorised
 * server-side. Nothing is persisted - a refresh sends the driver back to the keypad, which is
 * how the Vercel page behaves.
 */
export const useKeylessPortal = () => {
  const [pin, setPin] = useState<string | null>(null)
  const [vehicle, setVehicle] = useState<KeylessVehicle | null>(null)

  const validatePin = useCallback(async (enteredPin: string) => {
    const validatedVehicle = await invokeKeyless({ action: "validate-pin", pin: enteredPin })
    setPin(enteredPin)
    setVehicle(validatedVehicle)
  }, [])

  const sendCommand = useCallback(
    async (command: VehicleCommand) => {
      if (!pin) {
        throw new Error("Please enter your PIN before sending a command.")
      }

      const updatedVehicle = await invokeKeyless({ action: "vehicle-command", pin, command })
      setVehicle(updatedVehicle)
    },
    [pin],
  )

  /** Live state from INVERS, rather than the lock status Airtable last recorded. */
  const refreshVehicle = useCallback(async () => {
    if (!pin) {
      throw new Error("Please enter your PIN before refreshing.")
    }

    const refreshedVehicle = await invokeKeyless({ action: "refresh-vehicle", pin })
    setVehicle(refreshedVehicle)
  }, [pin])

  const signOut = useCallback(() => {
    setPin(null)
    setVehicle(null)
  }, [])

  return { vehicle, validatePin, sendCommand, refreshVehicle, signOut }
}
